import { useNavigate } from "react-router";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  selectHasSeenFirstLoginWelcome,
  setFirstLoginWelcomeSeen,
} from "@/store/slices/onboardingSlice";

/**
 * Welcome ViewModel Actions
 */
export interface WelcomeViewModelActions {
  /** Navigate to pairing help page and mark welcome as seen */
  goToPairing: () => void;
  /** Navigate to trophy room and mark welcome as seen */
  goToCollection: () => void;
}

/**
 * Welcome ViewModel Return Type
 */
export interface WelcomeViewModel {
  actions: WelcomeViewModelActions;
  onMount: () => void;
}

/**
 * useWelcomeViewModel Hook
 *
 * MVVM ViewModel for the Welcome page (first login experience).
 * Structure: { actions, onMount }
 *
 * Business Rules:
 * - If user has already seen welcome, redirect to trophy room
 * - Both actions navigate away and mark welcome as seen
 *
 * @returns ViewModel with welcome page logic
 */
export function useWelcomeViewModel(): WelcomeViewModel {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const hasSeenWelcome = useAppSelector(selectHasSeenFirstLoginWelcome);

  // Actions (all user intents)
  // Note: React Compiler automatically memoizes these functions
  const actions: WelcomeViewModelActions = {
    goToPairing: () => {
      dispatch(setFirstLoginWelcomeSeen());
      navigate("/pairing-help");
    },

    goToCollection: () => {
      dispatch(setFirstLoginWelcomeSeen());
      navigate("/owner/trophy-room");
    },
  };

  // Lifecycle
  // Note: React Compiler automatically memoizes this function
  const onMount = () => {
    if (hasSeenWelcome) {
      navigate("/owner/trophy-room");
    }
  };

  return { actions, onMount };
}
